import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFailedEmails, getPendingEmails } from '../../services/clientService';
import { formatRelativeDate } from '../../utils/relativeTime';
import {
  MAILS_LIMIT,
  traduireVerdict,
  dateEnvoi,
  trierParDate,
  compteursEnvois,
  badgesEnvoi,
  destinatairesEnvoi,
  envoiDeTest,
  compterEnvoisDeTest,
  listeTronquee
} from '../../utils/mailDelivery';

/**
 * Sous-onglet « Emails en échec » (lot I5) — LECTURE SEULE (DI5-9).
 *
 * Ce que l'écran montre : les envois du journal « mail » qui n'ont PAS abouti, TOUTES fiches
 * confondues — la réponse à « le client dit qu'il n'a rien reçu » sans ouvrir sa fiche au préalable.
 *
 * Ce que l'écran ne fait JAMAIS : aucun renvoi, aucune suppression, aucune annotation (le journal
 * « mail » est une PREUVE). Le renvoi d'un email est un lot SÉPARÉ (I2c, hors périmètre I5).
 *
 * Vérité de l'écran (héritée d'I2a) : un envoi « en attente » n'est JAMAIS présenté comme un échec
 * (DI5-7, bancs B1/B4) ⇒ la vue par défaut ne liste QUE les échecs, et l'attente n'apparaît que
 * sur un filtre explicite (chargée à la demande, donc sans coût de lecture par défaut).
 */

/** Pastille du verdict d'envoi — le libellé vient du module pur, jamais réinventé ici. */
const VerdictPill = ({ verdict }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${verdict.classe}`}>
    {verdict.label}
  </span>
);

/** Pastille d'information (environnement, renvoi assumé…). */
const MetaBadge = ({ badge }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.classe}`}>
    {badge.label}
  </span>
);

/** Étiquette « adresse de test » (DI5-6) : informatif, JAMAIS un masquage. */
const AdresseTestBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/20 text-secondary border border-secondary/30">
    adresse de test
  </span>
);

/** Vue par défaut : les échecs ; « en attente » (jamais un échec) sur demande (DI5-7). */
const FILTRES_STATUT = [
  { id: 'echecs', label: 'Échecs seulement' },
  { id: 'attente', label: 'En attente seulement' },
  { id: 'tous', label: 'Échecs + en attente' }
];

const dateComplete = (date) => (date ? date.toLocaleString('fr-FR') : '');

/** Une ligne : identité du client, gabarit, date, tentatives, erreur brute remontée par le serveur. */
const LigneEchec = ({ email }) => {
  const verdict = traduireVerdict(email.delivery);
  const date = dateEnvoi(email);
  const destinataires = destinatairesEnvoi(email.to);
  const badges = badgesEnvoi(email.meta);

  return (
    <li className="border border-base-200 rounded-lg bg-base-100 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {email.clientUid ? (
          <Link to={`/clients/${email.clientUid}`} className="link link-primary text-sm font-medium">
            {email.clientNom || 'Voir la fiche client'}
          </Link>
        ) : (
          <span className="text-sm text-base-content/60">client non rattaché</span>
        )}
        <VerdictPill verdict={verdict} />
        {envoiDeTest(email.to) && <AdresseTestBadge />}
        {badges.map((badge) => <MetaBadge key={badge.cle} badge={badge} />)}
        <span className="ml-auto text-xs text-base-content/60" title={dateComplete(date)}>
          {date ? formatRelativeDate(date) : 'date non tracée'}
        </span>
      </div>

      <div className="text-xs text-base-content/60 mt-1">
        {email.meta.templateName || 'gabarit inconnu'}
        {destinataires.length > 0 && <> · destinataire : {destinataires.join(', ')}</>}
        {destinataires.length === 0 && <> · destinataire non tracé</>}
        {email.delivery && email.delivery.attempts !== null && <> · tentatives : {email.delivery.attempts}</>}
      </div>

      {verdict.erreur && (
        <div className="bg-error/10 border border-error/30 rounded-lg p-2 mt-2">
          <p className="text-[11px] uppercase tracking-wide text-error">Erreur remontée par le serveur</p>
          <p className="text-xs font-mono text-error/90 break-all mt-0.5">{verdict.erreur}</p>
        </div>
      )}
    </li>
  );
};

const FailedEmailsTab = () => {
  const [echecs, setEchecs] = useState([]);
  // `null` = pas encore chargée : l'attente n'est lue QUE si le filtre la demande (DI5-7).
  const [attente, setAttente] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statut, setStatut] = useState('echecs');

  const chargerEchecs = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      setEchecs(await getFailedEmails());
    } catch (error) {
      console.warn('FailedEmailsTab: échecs indisponibles', error?.message);
      setLoadError('Impossible de charger les emails en échec.');
      setEchecs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { chargerEchecs(); }, [chargerEchecs]);

  useEffect(() => {
    if (statut === 'echecs' || attente !== null) return undefined;
    let actif = true;
    getPendingEmails()
      .then((liste) => { if (actif) setAttente(liste); })
      .catch((error) => {
        console.warn('FailedEmailsTab: attente indisponible', error?.message);
        if (actif) setAttente([]);
      });
    return () => { actif = false; };
  }, [statut, attente]);

  const visibles = useMemo(() => {
    if (statut === 'attente') return attente || [];
    if (statut === 'tous') return trierParDate([...(attente || []), ...echecs]);
    return echecs;
  }, [statut, attente, echecs]);

  const compteurs = useMemo(() => compteursEnvois(echecs), [echecs]);
  const nbAdressesDeTest = useMemo(() => compterEnvoisDeTest(echecs), [echecs]);
  const tronquee = listeTronquee(echecs, MAILS_LIMIT);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
        <span className="ml-3 text-base-content/70">Chargement des emails en échec...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-error/10 text-error p-4 rounded-md">
        <h3 className="font-bold">Emails en échec indisponibles</h3>
        <p className="text-sm">{loadError}</p>
        <p className="text-xs mt-1">
          Le journal « mail » est réservé aux comptes administrateurs : cette lecture a été refusée
          ou interrompue. Rien n’a été modifié.
        </p>
        <button type="button" className="btn btn-sm btn-error mt-2" onClick={chargerEchecs}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-base-content">Emails en échec</h3>
          <p className="text-sm text-base-content/70 mt-1">
            {compteurs.total} envoi(s) en échec
            {nbAdressesDeTest > 0 && ` · dont ${nbAdressesDeTest} vers une adresse de test`}
          </p>
        </div>
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info/20 text-info border border-info/30">
          lecture seule
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <select
          className="select select-bordered select-sm"
          value={statut}
          onChange={(event) => setStatut(event.target.value)}
          aria-label="Filtrer les envois par statut"
        >
          {FILTRES_STATUT.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </div>

      <p className="text-xs text-base-content/60 mt-3">
        Source : journal « mail » du projet portail, filtré sur les envois en ÉCHEC. Les envois « en
        attente » ne sont JAMAIS comptés comme des échecs : ils n’apparaissent que sur le filtre
        ci-dessus. Les adresses de test sont ÉTIQUETÉES, jamais masquées.
      </p>

      {visibles.length === 0 ? (
        <p className="text-sm text-base-content/60 py-6 text-center">
          {statut === 'echecs'
            ? 'Aucun envoi en échec : tout ce qui a été adressé est parti.'
            : statut === 'attente'
              ? 'Aucun envoi en attente.'
              : 'Aucun envoi en échec ni en attente.'}
        </p>
      ) : (
        <ul className="space-y-2 mt-3">
          {visibles.map((email) => <LigneEchec key={email.id} email={email} />)}
        </ul>
      )}

      <p className="text-xs text-base-content/50 mt-4">
        {`Lecture limitée à ${MAILS_LIMIT} envois par état.`}
        {tronquee && ' La borne est atteinte : d’autres envois en échec peuvent exister au-delà.'}
        {' Aucune action possible depuis cet écran : ni renvoi, ni suppression.'}
      </p>
    </div>
  );
};

export default FailedEmailsTab;

